import { gql, useLazyQuery, useMutation } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { ChevronDownIcon, ChevronUpIcon } from '@orn/shared-web/icons';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';

import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';

import type {
  AdminDeleteBankConnectionMutation,
  AdminDeleteBankConnectionMutationVariables,
  AdminGetFamilyBankConnectionsQuery,
  AdminGetFamilyBankConnectionsQueryVariables,
  AdminReSyncFinancialDataMutation,
  AdminReSyncFinancialDataMutationVariables,
} from './__generated__/BankConnectionsScene.gql';

const ADMIN_GET_FAMILY_BANK_CONNECTIONS = gql`
  query AdminGetFamilyBankConnections($familyId: ID!) {
    adminGetFamilyBankConnections(familyId: $familyId) {
      id
      externalId
      lastSyncedAt
      bank {
        id
        name
        logoUrl
      }
      user {
        id
        email
      }
      accounts {
        id
        name
        mask
        type
        subtype
      }
    }
    adminGetBankConnectionsHealth(familyId: $familyId) {
      bankConnectionId
      stale
      neverSynced
      plaidJSON
      error
    }
  }
`;

const ADMIN_RESYNC_FINANCIAL_DATA = gql`
  mutation AdminReSyncFinancialData($bankConnectionId: ID!, $resetCursor: Boolean) {
    adminReSyncFinancialData(bankConnectionId: $bankConnectionId, resetCursor: $resetCursor) {
      id
      lastSyncedAt
    }
  }
`;

const ADMIN_DELETE_BANK_CONNECTION = gql`
  mutation AdminDeleteBankConnection($bankConnectionId: ID!) {
    adminDeleteBankConnection(bankConnectionId: $bankConnectionId)
  }
`;

const formatLastSynced = (date: string | null | undefined): string => {
  if (!date) {
    return 'Never';
  }
  return new Date(date).toLocaleString();
};

/**
 * Bank Connections admin tool. Operators select a user, then view every Plaid
 * bank connection on their family with sync health (stale / never-synced /
 * Plaid item JSON), can resync the Plaid pipeline (with or without resetting
 * the transaction cursor), and can delete connections (revoking the Plaid
 * access token first). The selector is user-keyed for operator UX, but
 * queries and mutations send `familyId` over the wire — the data is family
 * scoped (see ORN-526).
 */
const BankConnectionsScene = () => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [bankConnectionToDelete, setBankConnectionToDelete] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = React.useState<Set<string>>(new Set());
  const [expandedHealthJSON, setExpandedHealthJSON] = React.useState<Set<string>>(new Set());

  const { showToast } = useToast();

  const [
    loadBankConnections,
    { data: bankConnectionsData, loading: bankConnectionsLoading, refetch: refetchConnections },
  ] = useLazyQuery<AdminGetFamilyBankConnectionsQuery, AdminGetFamilyBankConnectionsQueryVariables>(
    ADMIN_GET_FAMILY_BANK_CONNECTIONS
  );

  const [resyncFinancialData] = useMutation<
    AdminReSyncFinancialDataMutation,
    AdminReSyncFinancialDataMutationVariables
  >(ADMIN_RESYNC_FINANCIAL_DATA);

  const [deleteBankConnection] = useMutation<
    AdminDeleteBankConnectionMutation,
    AdminDeleteBankConnectionMutationVariables
  >(ADMIN_DELETE_BANK_CONNECTION);

  const familyId = selection?.familyId ?? null;

  React.useEffect(() => {
    if (familyId) {
      void loadBankConnections({ variables: { familyId } });
    }
  }, [familyId, loadBankConnections]);

  const healthStatusMap = React.useMemo(() => {
    const map = new Map<
      string,
      NonNullable<AdminGetFamilyBankConnectionsQuery['adminGetBankConnectionsHealth']>[number]
    >();
    bankConnectionsData?.adminGetBankConnectionsHealth?.forEach((status) => {
      map.set(status.bankConnectionId, status);
    });
    return map;
  }, [bankConnectionsData]);

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setDeleteModalOpen(false);
    setBankConnectionToDelete(null);
    setExpandedHealthJSON(new Set());
    setExpandedAccounts(new Set());
  };

  const toggleHealthJSONExpanded = (bankConnectionId: string) => {
    setExpandedHealthJSON((prev) => {
      const next = new Set(prev);
      if (next.has(bankConnectionId)) {
        next.delete(bankConnectionId);
      } else {
        next.add(bankConnectionId);
      }
      return next;
    });
  };

  const toggleAccountsExpanded = (bankConnectionId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(bankConnectionId)) {
        next.delete(bankConnectionId);
      } else {
        next.add(bankConnectionId);
      }
      return next;
    });
  };

  const runResync = async (bankConnectionId: string, resetCursor: boolean) => {
    if (!familyId) {
      showToast({ type: 'error', message: 'Select a user before resyncing.' });
      return;
    }
    setActionLoading(bankConnectionId);
    try {
      await resyncFinancialData({
        variables: { bankConnectionId, resetCursor },
      });
      await refetchConnections();
      showToast({
        type: 'success',
        message: resetCursor ? 'Resync complete (cursor reset).' : 'Resync complete.',
      });
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (bankConnectionId: string) => {
    setBankConnectionToDelete(bankConnectionId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bankConnectionToDelete || !familyId) {
      return;
    }

    setActionLoading(bankConnectionToDelete);
    try {
      await deleteBankConnection({
        variables: { bankConnectionId: bankConnectionToDelete },
      });
      setDeleteModalOpen(false);
      setBankConnectionToDelete(null);
      await refetchConnections();
      showToast({ type: 'success', message: 'Bank connection deleted.' });
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(null);
    }
  };

  const selectedUserEmail = bankConnectionsData?.adminGetFamilyBankConnections?.[0]?.user?.email ?? null;

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <div className="flex flex-col gap-1">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Bank connections</h1>
          <p className="text-fg-secondary text-xs">
            Select a user to inspect their family's Plaid bank connections, force a resync, or revoke + delete.
          </p>
        </div>

        <div className="w-full max-w-md">
          <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
        </div>

        {!familyId ? (
          <div className="text-fg-tertiary text-xs italic">Select a user to view bank connections.</div>
        ) : bankConnectionsLoading ? (
          <div className="text-fg-secondary text-xs">Loading bank connections…</div>
        ) : !bankConnectionsData?.adminGetFamilyBankConnections?.length ? (
          <div className="text-fg-tertiary text-xs italic">No bank connections found for this family.</div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="text-fg-secondary text-xs">
              {selectedUserEmail ? (
                <>
                  Bank connections for family of <span className="font-mono">{selectedUserEmail}</span>
                </>
              ) : (
                'Bank connections'
              )}
            </div>

            {bankConnectionsData.adminGetFamilyBankConnections.map((bankConnection) => {
              const healthStatus = healthStatusMap.get(bankConnection.id);
              const isAccountsExpanded = expandedAccounts.has(bankConnection.id);
              const isHealthExpanded = expandedHealthJSON.has(bankConnection.id);
              const isActing = actionLoading === bankConnection.id;

              const statusLabel = healthStatus
                ? healthStatus.error
                  ? 'Error'
                  : healthStatus.neverSynced
                    ? 'Never synced'
                    : healthStatus.stale
                      ? 'Stale'
                      : 'Healthy'
                : null;
              const statusBadgeClass = healthStatus
                ? healthStatus.error
                  ? 'bg-error-primary text-fg-error-primary'
                  : healthStatus.neverSynced || healthStatus.stale
                    ? 'bg-warning-primary text-fg-warning-primary'
                    : 'bg-success-primary text-fg-success-primary'
                : '';

              return (
                <div key={bankConnection.id} className="border-border-primary bg-bg-primary border px-3 py-2">
                  <div className="flex items-center gap-3">
                    {bankConnection.bank.logoUrl && (
                      <img
                        src={bankConnection.bank.logoUrl}
                        alt={bankConnection.bank.name}
                        className="border-border-primary h-6 w-6 shrink-0 border"
                      />
                    )}
                    <div className="flex items-baseline gap-2 text-sm font-semibold">
                      <span>{bankConnection.bank.name}</span>
                      {statusLabel && (
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statusBadgeClass}`}
                        >
                          {statusLabel}
                        </span>
                      )}
                    </div>
                    <div className="ml-auto flex items-center gap-1.5">
                      <Button
                        onClick={() => void runResync(bankConnection.id, false)}
                        isDisabled={isActing}
                        isLoading={isActing}
                        color="secondary"
                        size="xs"
                      >
                        Resync
                      </Button>
                      <Button
                        onClick={() => void runResync(bankConnection.id, true)}
                        isDisabled={isActing}
                        isLoading={isActing}
                        color="secondary"
                        size="xs"
                      >
                        Resync · reset cursor
                      </Button>
                      <Button
                        onClick={() => handleDeleteClick(bankConnection.id)}
                        isDisabled={isActing}
                        color="danger"
                        size="xs"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  <div className="text-fg-tertiary mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[11px]">
                    <span>
                      Synced {bankConnection.lastSyncedAt ? formatTimeAgo(bankConnection.lastSyncedAt) : 'never'}
                      {bankConnection.lastSyncedAt && (
                        <span className="text-fg-quaternary"> · {formatLastSynced(bankConnection.lastSyncedAt)}</span>
                      )}
                    </span>
                    <span>
                      <span className="text-fg-quaternary">plaid </span>
                      {bankConnection.externalId}
                    </span>
                    <span>
                      <span className="text-fg-quaternary">conn </span>
                      {bankConnection.id}
                    </span>
                  </div>

                  <div className="mt-1.5 flex items-center gap-3 text-xs">
                    {healthStatus && (
                      <button
                        type="button"
                        onClick={() => toggleHealthJSONExpanded(bankConnection.id)}
                        className="text-fg-secondary hover:text-fg-primary inline-flex items-center gap-1"
                      >
                        <span>Health JSON</span>
                        {isHealthExpanded ? (
                          <ChevronUpIcon className="size-3 shrink-0" />
                        ) : (
                          <ChevronDownIcon className="size-3 shrink-0" />
                        )}
                      </button>
                    )}
                    {bankConnection.accounts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleAccountsExpanded(bankConnection.id)}
                        className="text-fg-secondary hover:text-fg-primary inline-flex items-center gap-1"
                      >
                        <span>Accounts ({bankConnection.accounts.length})</span>
                        {isAccountsExpanded ? (
                          <ChevronUpIcon className="size-3 shrink-0" />
                        ) : (
                          <ChevronDownIcon className="size-3 shrink-0" />
                        )}
                      </button>
                    )}
                    {healthStatus?.error && (
                      <span className="text-fg-error-primary truncate">{healthStatus.error}</span>
                    )}
                  </div>

                  {isHealthExpanded && healthStatus && (
                    <pre className="bg-bg-secondary text-fg-primary mt-1.5 max-h-64 overflow-auto p-2 font-mono text-[10px] leading-snug">
                      {healthStatus.plaidJSON ? JSON.stringify(healthStatus.plaidJSON, null, 2) : 'No data'}
                    </pre>
                  )}

                  {isAccountsExpanded && bankConnection.accounts.length > 0 && (
                    <div className="mt-1.5 flex flex-col gap-px">
                      {bankConnection.accounts.map((account) => (
                        <div key={account.id} className="bg-bg-secondary flex items-baseline gap-3 px-2 py-1 text-xs">
                          <span className="font-medium">
                            {account.name}
                            {account.mask && <span className="text-fg-tertiary"> ···{account.mask}</span>}
                          </span>
                          <span className="text-fg-tertiary font-mono text-[11px]">
                            {account.type} · {account.subtype}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Modal isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen} size="md">
          <Modal.Header onClose={() => setDeleteModalOpen(false)}>Delete bank connection</Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <div className="text-fg-primary flex flex-col gap-2 text-sm">
                <div>Are you sure? This action cannot be undone.</div>
                <div>The connection will be revoked from Plaid and removed from the database.</div>
                {selectedUserEmail && (
                  <div className="text-fg-secondary text-xs">
                    The owner will need to log in and re-add the bank to restore this information; some historical data
                    may still be lost.
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setDeleteModalOpen(false)} color="secondary" size="sm">
                  Cancel
                </Button>
                <Button
                  onClick={() => void handleDeleteConfirm()}
                  isDisabled={Boolean(actionLoading)}
                  isLoading={Boolean(actionLoading)}
                  color="danger"
                  size="sm"
                >
                  Delete
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </main>
    </div>
  );
};

export default BankConnectionsScene;
